import { UserManager, Log, User } from "oidc-client-ts";
import { oidcConfig } from "./config";
import { isCrossTabLogoutInProgress } from "./crossTabSession";

Log.setLogger(console);
Log.setLevel(Log.DEBUG);

export const oidcUserManager = new UserManager(oidcConfig);

// Coordinate renewals across ODP tabs without changing SiteMinder settings.
// BroadcastChannel is limited to pages on the same browser origin.
const channel = typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel("odp-siteminder-session-v1")
  : null;
const rawSigninSilent = oidcUserManager.signinSilent.bind(oidcUserManager);
const instanceId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const waiters = new Set<(user: User) => void>();

const validUser = (user: User | null): user is User =>
  !!user && !user.expired && !!user.access_token;

async function applyPeerUser(serialized: string): Promise<User | null> {
  if (isCrossTabLogoutInProgress()) return null;
  try {
    const incoming = User.fromStorageString(serialized);
    if (!validUser(incoming)) return null;
    const current = await oidcUserManager.getUser();
    if (isCrossTabLogoutInProgress()) return null;
    // Never switch authenticated identities in response to a tab message.
    if (!current || current.profile.sub !== incoming.profile.sub) return null;
    if ((current.expires_at ?? 0) > (incoming.expires_at ?? 0)) return current;
    if (isCrossTabLogoutInProgress()) return null;
    await oidcUserManager.storeUser(incoming);
    if (isCrossTabLogoutInProgress()) {
      await oidcUserManager.removeUser();
      return null;
    }
    sessionStorage.setItem("access_token", incoming.access_token);
    if (incoming.id_token) sessionStorage.setItem("id_token", incoming.id_token);
    if (incoming.expires_at) sessionStorage.setItem("accessTokenExp", String(incoming.expires_at));
    sessionStorage.setItem("authenticated", "true");
    window.dispatchEvent(new Event("odp:peer-session-renewed"));
    return incoming;
  } catch (error) {
    console.warn("[CrossTabSession] Failed applying peer session", error);
    return null;
  }
}

if (channel) {
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data;
    if (isCrossTabLogoutInProgress() || !data || data.source === instanceId || data.type !== "renewed" ||
        typeof data.user !== "string") return;
    void applyPeerUser(data.user).then((user) => {
      if (user && !isCrossTabLogoutInProgress()) for (const resolve of waiters) resolve(user);
    });
  };
}

// Preserve oidc-client-ts' original sign-in behavior while serializing
// concurrent refresh-token usage across tabs in browsers supporting Web Locks.
// The callback re-reads the user after obtaining the lock because another tab
// may have completed renewal while this tab was waiting.
oidcUserManager.signinSilent = (async (...args: Parameters<UserManager["signinSilent"]>) => {
  if (isCrossTabLogoutInProgress()) return null;
  const before = await oidcUserManager.getUser();
  if (isCrossTabLogoutInProgress()) return null;
  const beforeExp = before?.expires_at ?? 0;
  const renew = async (): Promise<User | null> => {
    if (isCrossTabLogoutInProgress()) return null;
    const current = await oidcUserManager.getUser();
    if (isCrossTabLogoutInProgress()) return null;
    if (validUser(current) && (current.expires_at ?? 0) > beforeExp) return current;
    const refreshed = await rawSigninSilent(...args);
    if (isCrossTabLogoutInProgress()) {
      // oidc-client-ts may have stored the result before returning it.
      await oidcUserManager.removeUser();
      return null;
    }
    if (validUser(refreshed)) {
      channel?.postMessage({ type: "renewed", source: instanceId,
        user: refreshed.toStorageString() });
    }
    return refreshed;
  };
  if (!channel || !navigator.locks?.request) return renew();
  return navigator.locks.request("odp-siteminder-renew-v1", async () => {
    if (isCrossTabLogoutInProgress()) return null;
    // Allow the tab which just renewed to broadcast its OIDC user.
    const peer = await new Promise<User | null>((resolve) => {
      let settled = false;
      const handler = (user: User) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        waiters.delete(handler);
        resolve(user);
      };
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        waiters.delete(handler);
        resolve(null);
      }, 350);
      waiters.add(handler);
    });
    if (isCrossTabLogoutInProgress()) return null;
    if (peer && (peer.expires_at ?? 0) > beforeExp) return peer;
    return renew();
  });
}) as UserManager["signinSilent"];
